/**
 * Display plate helpers for Assets.
 *
 * Keep in sync with zeecore/src/truth/identity/vehicleIdentifiers.ts
 * (`parseCompoundVehicleReg` / `displayPlateFromRegistration`) — the app
 * bundle cannot import zeecore directly.
 */

function alnumKey(raw: string): string {
  return raw.normalize('NFKC').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function isDigitsOnly(token: string): boolean {
  return /^\d+$/.test(token);
}

function hasLetter(token: string): boolean {
  return /[A-Z]/i.test(token);
}

/** Split Aquarius compound current_reg into plate / fleet tokens. */
export function splitCompoundVehicleReg(raw: string): string[] {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return [];

  if (/[|/]/.test(trimmed)) {
    return trimmed
      .split(/[|/]+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => alnumKey(t) || t.replace(/\s+/g, '').toUpperCase())
      .filter(Boolean);
  }

  const hyphen = trimmed.indexOf('-');
  if (hyphen > 0 && hyphen < trimmed.length - 1) {
    const left = trimmed.slice(0, hyphen).trim();
    const right = trimmed.slice(hyphen + 1).trim();
    if (!left.includes('-') && !right.includes('-')) {
      const leftKey = alnumKey(left);
      const rightKey = alnumKey(right);
      if (leftKey && rightKey && leftKey.length >= 3 && rightKey.length >= 3) {
        const leftDigits = isDigitsOnly(leftKey);
        const rightDigits = isDigitsOnly(rightKey);
        const leftLetter = hasLetter(leftKey);
        const rightLetter = hasLetter(rightKey);
        if ((leftLetter && rightDigits) || (rightLetter && leftDigits)) {
          return [leftKey, rightKey];
        }
      }
    }
  }

  const single = alnumKey(trimmed) || trimmed.replace(/\s+/g, '').toUpperCase();
  return single ? [single] : [];
}

export function parseCompoundVehicleReg(raw: string): {
  registration?: string;
  fleet_id?: string;
  tokens: string[];
} {
  const tokens = splitCompoundVehicleReg(raw);
  if (tokens.length === 0) return { tokens };
  if (tokens.length === 1) {
    const t = tokens[0];
    if (isDigitsOnly(t)) return { tokens, fleet_id: t };
    return { tokens, registration: t };
  }
  const a = tokens[0];
  const b = tokens[1];
  if (hasLetter(a) && isDigitsOnly(b)) return { tokens, registration: a, fleet_id: b };
  if (hasLetter(b) && isDigitsOnly(a)) return { tokens, registration: b, fleet_id: a };
  if (hasLetter(a) && !hasLetter(b)) return { tokens, registration: a, fleet_id: b };
  if (hasLetter(b) && !hasLetter(a)) return { tokens, registration: b, fleet_id: a };
  return { tokens, registration: a, fleet_id: b };
}

/**
 * Prefer a road plate when a registration string is Aquarius compound form.
 * Ordinary plates (incl. hyphenated UK regs) are returned unchanged.
 */
export function displayPlateFromRegistration(raw: string | null | undefined): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  const parsed = parseCompoundVehicleReg(trimmed);
  if (parsed.registration && parsed.fleet_id) return parsed.registration;
  return trimmed;
}

/** Digits-only fleet / internal code (e.g. Aquarius historical `591104`). */
export function isDigitsOnlyRegistration(raw: string | null | undefined): boolean {
  const key = alnumKey(String(raw ?? '').trim());
  return Boolean(key) && isDigitsOnly(key);
}

/**
 * Fleet / internal codes claimed on a vehicle that can join Aquarius digits-only
 * rows to a TIP plate row (Pass-2 client safety net).
 *
 * Includes explicit `fleet_id` from tip/aquarius and fleet tokens parsed out of
 * Aquarius compound registrations.
 */
export function fleetIdsForTipAquariusJoin(
  identifiers: Array<{ type?: string; value?: string; sourceSystem?: string }> | undefined,
): Set<string> {
  const out = new Set<string>();
  for (const id of identifiers ?? []) {
    const source = String(id.sourceSystem ?? '').toLowerCase();
    if (source !== 'tip' && source !== 'aquarius') continue;
    const type = String(id.type ?? '').toLowerCase();
    const raw = String(id.value ?? '').trim();
    if (!raw) continue;

    if (type === 'fleet_id') {
      const fleet = alnumKey(raw);
      if (fleet && isDigitsOnly(fleet)) out.add(fleet);
      continue;
    }
    if (type === 'registration' || type === 'vrn' || type === 'vehicle_registration') {
      const parsed = parseCompoundVehicleReg(raw);
      if (parsed.fleet_id && isDigitsOnly(parsed.fleet_id)) out.add(parsed.fleet_id);
    }
  }
  return out;
}
